import {
  BookOpen,
  Briefcase,
  Calculator,
  HeartPulse,
  Landmark,
  PiggyBank,
  ShieldCheck,
  Users,
} from "lucide-react";

export const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Meet Alicia", href: "/about" },
  { label: "Resources", href: "/resources" },
  { label: "Contact", href: "/contact" },
];

export const images = {
  hero: "https://images.pexels.com/photos/1648358/pexels-photo-1648358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  alicia: "https://images.pexels.com/photos/27086907/pexels-photo-27086907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  retirement: "https://images.pexels.com/photos/12013184/pexels-photo-12013184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1100",
  family: "https://images.unsplash.com/photo-1738747726413-866aa0023653?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  contact: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  meeting: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1100",
};

export const services = [
  {
    number: "01",
    slug: "life-insurance",
    title: "Life Insurance",
    icon: ShieldCheck,
    summary: "Thoughtful coverage that helps your family stay secure when life changes.",
    description:
      "Alicia helps you understand term, whole life, and blended options in plain language, then aligns coverage with your family, income, debts, and long-term goals.",
    points: ["Needs-based policy reviews", "Income and mortgage protection", "Clear beneficiary guidance"],
  },
  {
    number: "02",
    slug: "health-insurance",
    title: "Health Insurance",
    icon: HeartPulse,
    summary: "Compassionate guidance through plans, benefits, and enrollment decisions.",
    description:
      "From individual and family coverage to Medicare-related conversations, Bello Joy Consulting helps you compare options without pressure or confusion.",
    points: ["Plan comparison support", "Enrollment education", "Coverage gap reviews"],
  },
  {
    number: "03",
    slug: "retirement-planning",
    title: "Retirement Planning",
    icon: PiggyBank,
    summary: "Purposeful strategies for income, confidence, and the future you envision.",
    description:
      "Alicia connects protection planning with retirement goals so each decision supports both today’s responsibilities and tomorrow’s independence.",
    points: ["Retirement readiness conversations", "Income protection strategies", "Long-term goal alignment"],
  },
  {
    number: "04",
    slug: "family-protection",
    title: "Family Protection Strategies",
    icon: Users,
    summary: "Layered protection for the people, milestones, and dreams you love most.",
    description:
      "Your family’s story shapes the strategy. Alicia listens first, then builds practical protection around children, aging parents, education, and legacy goals.",
    points: ["Family needs assessments", "Education funding conversations", "Multi-generational planning"],
  },
  {
    number: "05",
    slug: "business-insurance",
    title: "Business Insurance",
    icon: Briefcase,
    summary: "Protection for the business you have built and the people who depend on it.",
    description:
      "Bello Joy Consulting helps business owners evaluate continuity, key-person, and employee-related protection with steady, informed guidance.",
    points: ["Key-person protection", "Business continuity reviews", "Owner and family coordination"],
  },
  {
    number: "06",
    slug: "financial-consulting",
    title: "Financial Consulting",
    icon: Calculator,
    summary: "Clear, stewardship-minded conversations about protection and priorities.",
    description:
      "Alicia makes complex financial decisions easier to understand by connecting values, budget, risk, and long-term purpose into one practical plan.",
    points: ["Protection-first planning", "Budget-conscious recommendations", "Annual review guidance"],
  },
  {
    number: "07",
    slug: "legacy-estate-planning",
    title: "Legacy & Estate Planning",
    icon: Landmark,
    summary: "Prepare today so your care, values, and resources continue tomorrow.",
    description:
      "Through insurance-led legacy strategies and coordination with your professional advisors, Alicia helps families prepare for a meaningful transfer of care and resources.",
    points: ["Legacy goal conversations", "Beneficiary coordination", "Estate planning referrals"],
  },
];

export const values = [
  {
    title: "Faith in God",
    description: "Serving with honesty, wisdom, compassion, and a spirit of stewardship.",
  },
  {
    title: "Family",
    description: "Protecting one of life’s greatest blessings with thoughtful guidance.",
  },
  {
    title: "Customers First",
    description: "Listening carefully and creating solutions around each client’s needs.",
  },
];

export const promises = [
  {
    number: "01",
    title: "Listen before recommending",
    description: "Every conversation begins with your story, your concerns, and your goals.",
  },
  {
    number: "02",
    title: "Educate instead of pressure",
    description: "You receive clear explanations and honest options, never rushed sales tactics.",
  },
  {
    number: "03",
    title: "Personalize every strategy",
    description: "Your protection plan is shaped around your family, business, budget, and future.",
  },
  {
    number: "04",
    title: "Stay present through life",
    description: "Alicia remains available through milestones, changes, claims questions, and reviews.",
  },
  {
    number: "05",
    title: "Treat you like family",
    description: "Every client receives the same care and respect Alicia would give her own family.",
  },
];

export const testimonials = [
  {
    quote:
      "Alicia took time to understand our family before she ever mentioned a policy. We left the conversation feeling informed, peaceful, and genuinely cared for.",
    name: "Monica R.",
    detail: "Family protection client",
  },
  {
    quote:
      "She explained everything in language we could understand and helped us make a confident decision for our future. It never felt like a sales call.",
    name: "James & Elaine T.",
    detail: "Retirement planning clients",
  },
  {
    quote:
      "Alicia has been a steady advocate for our business and our family. Her integrity and follow-through are why we continue to trust her year after year.",
    name: "Derrick W.",
    detail: "Business insurance client",
  },
];

export const resources = [
  {
    slug: "term-vs-whole-life-insurance",
    category: "Insurance Education",
    title: "Term vs. whole life insurance: how to think about the difference",
    excerpt:
      "A calm, practical look at how each type of coverage can support family protection and long-term goals.",
    readTime: "5 min read",
    icon: BookOpen,
  },
  {
    slug: "five-questions-family-coverage",
    category: "Family Protection",
    title: "Five questions every family should ask before choosing coverage",
    excerpt:
      "Use these questions to clarify who depends on you, what needs protection, and how much peace of mind costs.",
    readTime: "4 min read",
    icon: Users,
  },
  {
    slug: "protection-planning-retirement",
    category: "Financial Wisdom",
    title: "Why protection planning belongs in every retirement conversation",
    excerpt:
      "Retirement is not only about growth. It is also about protecting income, health, and independence.",
    readTime: "6 min read",
    icon: PiggyBank,
  },
  {
    slug: "planning-with-purpose-stewardship",
    category: "Stewardship",
    title: "Planning with purpose: aligning money decisions with your values",
    excerpt:
      "A faith-centered reflection on stewardship, generosity, and preparing well for the people you love.",
    readTime: "4 min read",
    icon: HeartPulse,
  },
  {
    slug: "business-continuity-key-person",
    category: "Business Protection",
    title: "What would happen to your business if you could not be there?",
    excerpt:
      "Key-person and continuity planning can help protect employees, partners, and the family behind the business.",
    readTime: "5 min read",
    icon: Briefcase,
  },
  {
    slug: "beneficiary-conversations-legacy",
    category: "Legacy Planning",
    title: "Beneficiaries, wills, and the conversations families avoid",
    excerpt:
      "Small decisions today can spare your loved ones confusion and help your wishes be carried out with care.",
    readTime: "7 min read",
    icon: Landmark,
  },
];
